import * as Sentry from '@sentry/react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { ApiClient } from '../services/apiClient';
import type { PaymentQuery } from '../utils/query';
import type { FlowStep, OperationStatusResponse } from '../universal-flow/types';
import { interpretResponse } from '../universal-flow/flowInterpreter';

export type FlowState =
  | { status: 'bootstrapping' }
  | { status: 'ready'; step: FlowStep }
  | { status: 'error'; message: string; details?: string; canRetry?: boolean };

export type PaymentFlowActions = {
  refresh: () => Promise<void>;
  submitExtra: (extra: Record<string, unknown>) => Promise<void>;
};

/**
 * Проверяет, что строка не пустая (не undefined, не null, не пустая строка)
 */
function isNonEmptyString(value: string | undefined | null): value is string {
  return value !== undefined && value !== null && value !== '';
}

export function usePaymentFlow(query: PaymentQuery, infoTitle?: string): [FlowState, PaymentFlowActions] {
  const missingReason = useMemo((): string | null => {
    if (!isNonEmptyString(query.merchantId)) {
      return 'В ссылке отсутствует merchant_id.';
    }
    if (!isNonEmptyString(query.payload)) {
      return 'В ссылке отсутствует зашифрованный payload.';
    }
    return null;
  }, [query.merchantId, query.payload]);

  const [flowState, setFlowState] = useState<FlowState>(() => {
    if (missingReason !== null && missingReason !== undefined) {
      Sentry.captureMessage(missingReason, 'warning');
      return { status: 'error', message: missingReason, details: missingReason, canRetry: false };
    }
    return { status: 'bootstrapping' };
  });

  const apiClient = useMemo(() => new ApiClient(query), [query]);

  const resolveAndSetStep = useCallback(
    (response: OperationStatusResponse) => {
      const step = interpretResponse(response, infoTitle);
    setFlowState({ status: 'ready', step });
    },
    [infoTitle]
  );

  const refresh = useCallback(async (): Promise<void> => {
    if (missingReason !== null && missingReason !== undefined) {
      Sentry.captureMessage(missingReason, 'warning');
      setFlowState({ status: 'error', message: missingReason, details: missingReason, canRetry: false });
      return;
    }
    try {
      const response = await apiClient.loadStatus();
      resolveAndSetStep(response);
    } catch (error) {
      Sentry.captureException(error);
      const { summary, details } = normalizeError(error);
      setFlowState({
        status: 'error',
        message: summary,
        details,
        canRetry: !isPayloadValidationError(summary, details)
      });
    }
  }, [apiClient, resolveAndSetStep, missingReason]);

  const submitExtra = useCallback(
    async (extra: Record<string, unknown>): Promise<void> => {
      if (missingReason !== null && missingReason !== undefined) {
        Sentry.captureMessage(missingReason, 'warning');
        setFlowState({ status: 'error', message: missingReason, details: missingReason, canRetry: false });
        return;
      }
      try {
        const response = await apiClient.loadStatus(extra);
        resolveAndSetStep(response);
      } catch (error) {
        Sentry.captureException(error);
        const { summary, details } = normalizeError(error);
        setFlowState({
          status: 'error',
          message: summary,
          details,
          canRetry: !isPayloadValidationError(summary, details)
        });
      }
    },
    [apiClient, resolveAndSetStep, missingReason]
  );

  useEffect(() => {
    if (missingReason === null || missingReason === undefined) {
      void refresh();
    }
  }, [missingReason, refresh]);

  return [flowState, { refresh, submitExtra }];
}

function normalizeError(error: unknown): { summary: string; details: string } {
  const rawMessage =
    error instanceof Error ? error.message : typeof error === 'string' ? error : 'Unknown error';

  let summary = rawMessage;
  let details = rawMessage;

  const candidatePayload = extractJsonCandidate(rawMessage);

  if (candidatePayload !== null && candidatePayload !== undefined) {
    try {
    const parsed = JSON.parse(candidatePayload);
    const extracted = extractSummaryFromPayload(parsed);
    summary = extracted ?? summary;
    details = JSON.stringify(parsed, null, 2);
    } catch {
      // ignore malformed payloads
    }
  }

  return { summary, details };
}

function isPayloadValidationError(summary: string, details: string): boolean {
  const source = `${summary}\n${details}`.toLowerCase();
  const markers = [
    'invalid payload',
    'payload signature mismatch',
    'unable to decrypt payload',
    'payload expired',
    'invalid payload encoding',
    'payload is too short',
    'decrypted payload is not an object'
  ];

  return markers.some((marker) => source.includes(marker));
}

function extractJsonCandidate(message: string): string | null {
  const colonIndex = message.indexOf(':');
  const afterColon = colonIndex >= 0 ? message.slice(colonIndex + 1).trim() : message.trim();

  if (looksLikeJson(afterColon)) {
    return afterColon;
  }

  const match = message.match(/\{[\s\S]*\}$/);
  if (match && looksLikeJson(match[0])) {
    return match[0];
  }

  return null;
}

function looksLikeJson(value: string): boolean {
  if (value === null || value === undefined || value === '') {
    return false;
  }
  const trimmed = value.trim();
  return (
    (trimmed.startsWith('{') && trimmed.endsWith('}')) ||
    (trimmed.startsWith('[') && trimmed.endsWith(']'))
  );
}

function extractSummaryFromPayload(payload: unknown): string | undefined {
  if (payload === null || payload === undefined) {
    return undefined;
  }

  if (Array.isArray(payload)) {
    const messages = payload
      .map((item) => extractSummaryFromPayload(item))
      .filter((value): value is string => typeof value === 'string' && value.trim().length > 0);
    return messages.length > 0 ? messages[0] : undefined;
  }

  if (typeof payload === 'object') {
    const record = payload as Record<string, unknown>;

    const endpoints = ['context', 'provider', 'Unipesa'];
    for (const endpoint of endpoints) {
      const endpointValue = record[endpoint];
      if (endpointValue !== null && endpointValue !== undefined && typeof endpointValue === 'object') {
        const nested = extractSummaryFromPayload(endpointValue);
        if (nested !== null && nested !== undefined && nested.trim().length > 0) {
          return nested;
        }
      }
    }

    const flowError = record.flow_error;
    if (flowError !== null && flowError !== undefined && typeof flowError === 'object') {
      const normalized = Object.entries(flowError as Record<string, unknown>)
        .map(([name, value]) => {
          if (typeof value === 'string') {
            return `${name}: ${value}`;
          }
          return undefined;
        })
        .filter((value): value is string => typeof value === 'string' && value.trim().length > 0);

      if (normalized.length > 0) {
        return normalized.join('\n');
      }
    }

    const recordMessage =
      typeof record.message === 'string' && record.message.trim().length > 0 ? record.message : null;
    const recordError =
      typeof record.error === 'string' && record.error.trim().length > 0 ? record.error : null;
    const recordCode =
      typeof record.code === 'string' && record.code.trim().length > 0 ? record.code : null;

    return recordMessage ?? recordError ?? recordCode ?? extractMessage(payload);
  }

  if (typeof payload === 'string') {
    return payload;
  }

  return undefined;
}

function extractMessage(entry: unknown): string | undefined {
  if (entry === null || entry === undefined) {
    return undefined;
  }

  if (typeof entry === 'string') {
    return entry;
  }

  if (Array.isArray(entry)) {
    for (const item of entry) {
      const nested = extractMessage(item);
      if (nested !== null && nested !== undefined && nested.trim().length > 0) {
        return nested;
      }
    }
    return undefined;
  }

  if (typeof entry === 'object') {
    const record = entry as Record<string, unknown>;
    const keysToCheck: Array<keyof typeof record> = ['message', 'error', 'reason', 'code'];
    for (const key of keysToCheck) {
      const value = record[key];
      if (typeof value === 'string' && value.trim().length > 0) {
        return value;
      }
    }

    if (record.context !== null && record.context !== undefined && typeof record.context === 'object') {
      return extractMessage(record.context);
    }
  }

  return undefined;
}

